import { handleUnauthorizedResponse } from "./authSession";
import { API_CONFIG } from "../config/appConfig";

/**
 * API 호출을 위한 기본 fetch 래퍼
 * @param {string} endpoint - API 엔드포인트 (예: '/api/admin/auth/login')
 * @param {Object} options - fetch 옵션
 * @returns {Promise} - API 응답 Promise
 */
export const apiCall = async (endpoint, options = {}) => {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;

  const config = {
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    ...options,
  };

  // JSON 바디 직렬화 (object 전달 시)
  if (config.body && typeof config.body === "object") {
    config.body = JSON.stringify(config.body);
  }

  try {
    const response = await fetch(url, config);
    const authorization = config.headers?.Authorization;
    handleUnauthorizedResponse(response, authorization?.replace(/^Bearer\s+/i, ""));

    if (response.status === 204) {
      // No Content 응답인 경우 빈 객체 반환
      return {};
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
      // 상태코드 보존 (401 등을 상위에서 구분 처리)
      err.status = response.status;
      // 서버에서 코드 제공 시 보존
      if (errorData.code) err.code = errorData.code;
      throw err;
    }

    return await response.json();
  } catch (error) {
    console.error("API call error:", error);
    throw error;
  }
};

/**
 * 인증이 필요한 API 호출
 * @param {string} endpoint - API 엔드포인트
 * @param {Object} options - fetch 옵션
 * @param {string} token - 인증 토큰
 * @returns {Promise} - API 응답 Promise
 */
export const authenticatedApiCall = async (endpoint, options = {}, token) => {
  if (!token) {
    throw new Error("인증 토큰이 필요합니다.");
  }

  return apiCall(endpoint, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * 관리자 로그인 API 호출
 * @param {string} email - 이메일
 * @param {string} password - 비밀번호
 * @returns {Promise} - 로그인 결과
 */
export const loginAdmin = async (email, password) => {
  return apiCall("/api/admin/auth/login", {
    method: "POST",
    body: { email, password },
  });
};

/**
 * 관리자 정보 업데이트 API 호출
 * @param {Object} updateData - 업데이트할 데이터
 * @param {string} token - 인증 토큰
 * @returns {Promise} - 업데이트 결과
 */
export const updateAdminCredentials = async (updateData, token) => {
  return authenticatedApiCall(
    "/api/admin/auth/me/credentials",
    {
      method: "PUT",
      body: updateData,
    },
    token
  );
};

/**
 * 토큰 유효성 검증 (선택적)
 * @param {string} token - 검증할 토큰
 * @returns {Promise} - 검증 결과
 */
export const validateToken = async (token) => {
  try {
    return await authenticatedApiCall(
      "/api/admin/auth/validate",
      {
        method: "GET",
      },
      token
    );
  } catch (error) {
    return { valid: false, error: error.message };
  }
};

// ==============================================
// FAQ 관리 API
// ==============================================

/**
 * FAQ 생성
 * @param {Object} faqData - FAQ 데이터 { title, body }
 * @param {string} token - 인증 토큰
 * @returns {Promise} - 생성 결과
 */
export const createFAQ = async (faqData, token) => {
  console.log(faqData);
  return authenticatedApiCall(
    "/api/admin/faqs",
    {
      method: "POST",
      body: faqData,
    },
    token
  );
};

/**
 * FAQ 수정
 * @param {string} id - FAQ ID
 * @param {Object} updateData - 수정할 데이터 { title?, body? }
 * @param {string} token - 인증 토큰
 * @returns {Promise} - 수정 결과
 */
export const updateFAQ = async (id, updateData, token) => {
  return authenticatedApiCall(
    `/api/admin/faqs/${id}`,
    {
      method: "PUT",
      body: updateData,
    },
    token
  );
};

/**
 * FAQ 삭제
 * @param {string} id - FAQ ID
 * @param {string} token - 인증 토큰
 * @returns {Promise} - 삭제 결과
 */
export const deleteFAQ = async (id, token) => {
  return authenticatedApiCall(
    `/api/admin/faqs/${id}`,
    {
      method: "DELETE",
    },
    token
  );
};

// ==============================================
// 공지사항 관리 API
// ==============================================

/**
 * 공지사항 생성
 * @param {Object} noticeData - 공지사항 데이터 { title, body }
 * @param {string} token - 인증 토큰
 * @returns {Promise} - 생성 결과
 */
export const createNotice = async (noticeData, token) => {
  return authenticatedApiCall(
    "/api/admin/notices",
    {
      method: "POST",
      body: noticeData,
    },
    token
  );
};

/**
 * 공지사항 수정
 * @param {string} id - 공지사항 ID
 * @param {Object} updateData - 수정할 데이터 { title?, body? }
 * @param {string} token - 인증 토큰
 * @returns {Promise} - 수정 결과
 */
export const updateNotice = async (id, updateData, token) => {
  return authenticatedApiCall(
    `/api/admin/notices/${id}`,
    {
      method: "PUT",
      body: updateData,
    },
    token
  );
};

/**
 * 공지사항 삭제
 * @param {string} id - 공지사항 ID
 * @param {string} token - 인증 토큰
 * @returns {Promise} - 삭제 결과
 */
export const deleteNotice = async (id, token) => {
  return authenticatedApiCall(
    `/api/admin/notices/${id}`,
    {
      method: "DELETE",
    },
    token
  );
};

// ==============================================
// 사용자 통계 API
// ==============================================

/**
 * 사용자 통계 조회
 * @param {string} token - 인증 토큰
 * @returns {Promise} - 통계 데이터
 */
export const getUserStats = async (token) => {
  return authenticatedApiCall(
    "/api/users/stats",
    {
      method: "GET",
    },
    token
  );
};

// ==============================================
// 회원 관리 API
// ==============================================

/**
 * 회원 목록 조회 (검색/페이지네이션)
 * @param {Object} params - { limit, startAfter, search, searchField }
 * @param {string} token - 인증 토큰
 */
export const fetchUsers = async (params = {}, token) => {
  const query = new URLSearchParams();
  if (params.limit) query.append("limit", String(params.limit));
  if (params.startAfter) query.append("startAfter", params.startAfter);
  if (params.search) query.append("search", params.search);

  const qs = query.toString();
  const endpoint = `/api/admin/users${qs ? `?${qs}` : ""}`;

  return authenticatedApiCall(
    endpoint,
    {
      method: "GET",
    },
    token
  );
};

/**
 * 회원 삭제 (소프트 삭제 + 인증 비활성화)
 * @param {string} userId - 삭제할 유저 ID
 * @param {string} token - 인증 토큰
 */
export const deleteUser = async (userId, token) => {
  if (!userId) throw new Error("User ID is required.");
  return authenticatedApiCall(
    `/api/admin/users/${encodeURIComponent(userId)}`,
    {
      method: "DELETE",
    },
    token
  );
};
